import { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { HMIDataType, HMIField, HMIFieldKind, HMIFieldOption, HMIImageOverlayItem, HMIRestApiMethod, HMITemplate } from '../../types';
import {
  ACCESS_OPTIONS,
  ACTION_BEHAVIOR_OPTIONS,
  ARRAY_OF_OPTIONS,
  DATATYPE_OPTIONS,
  FIELD_KIND_OPTIONS,
  HMI_CHART_TYPE_OPTIONS,
  REST_API_METHOD_OPTIONS,
  SOURCE_KIND_OPTIONS,
} from './fieldOptions';

export type FieldDraft = Omit<HMIField, 'id' | 'screen' | 'group' | 'order'>;

export interface FieldEditorDialogProps {
  /** Present when editing an existing field; absent when adding a new one. */
  field?: HMIField;
  /**
   * Pre-selects the "kind" when adding a field from the section's "Add
   * widget" shortcut (always `"chart"`) vs. the generic "Add field" button.
   */
  defaultKind?: HMIFieldKind;
  /** Full template - used to list candidate "compare to" fields for dual-series charts, labeled by their section. */
  template: HMITemplate;
  onCancel: () => void;
  onSave: (draft: FieldDraft) => void;
  onDelete?: () => void;
}

const NUMERIC_TYPES: HMIDataType[] = ['i32', 'f32', 'f64'];

function defaultDatatypeFor(kind: HMIFieldKind): HMIDataType {
  if (kind === 'chart') return 'f64';
  if (kind === 'image') return 'string';
  return 'f32';
}

function emptyDraft(defaultKind?: HMIFieldKind): FieldDraft {
  const kind = defaultKind ?? 'indicator';
  return {
    caption: '',
    kind,
    datatype: defaultDatatypeFor(kind),
    access: kind === 'action' ? 'write' : 'read',
    sourceKind: kind === 'image' ? 'config' : 'live',
    chartType: kind === 'chart' ? 'line' : undefined,
    actionBehavior: kind === 'action' ? 'none' : undefined,
    overlayItems: kind === 'image' ? [] : undefined,
  };
}

/**
 * Add / edit / delete form for one `HMIField`, covering every property the
 * schema supports (see `src/hmi/types.ts`) so authoring a section's fields
 * no longer requires dropping down to the Advanced (JSON) tab. The "Add
 * widget" entry point (section toolbar) opens this same dialog pre-set to
 * `kind: "chart"`, reusing the dashboard's chart-type vocabulary via
 * `HMI_CHART_TYPE_OPTIONS`.
 */
function FieldEditorDialog({
  field,
  defaultKind,
  template,
  onCancel,
  onSave,
  onDelete,
}: FieldEditorDialogProps) {
  const isEditing = Boolean(field);
  const [draft, setDraft] = useState<FieldDraft>(
    () => field ?? emptyDraft(defaultKind)
  );
  const [optionDraft, setOptionDraft] = useState({ label: '', value: '' });
  const [headerDraft, setHeaderDraft] = useState({ key: '', value: '' });
  const [mappingDraft, setMappingDraft] = useState({ fieldId: '', responsePath: '' });

  const title = useMemo(() => {
    if (isEditing) return 'Edit field';
    if (defaultKind === 'chart') return 'Add widget / graph';
    if (defaultKind === 'image') return 'Add image widget';
    return 'Add field';
  }, [isEditing, defaultKind]);

  // Candidate fields a `kind: "image"` widget's pins can reference - any
  // field in the template with a live value (so not another image widget or
  // an action button), labeled with its section like the chart "compare to" list.
  const imageOverlayCandidates = useMemo(
    () =>
      template.fields
        .filter((f) => f.kind !== 'image' && f.kind !== 'action' && f.id !== field?.id)
        .map((f) => ({
          id: f.id,
          caption: f.caption,
          label: `${template.groups.find((g) => g.key === f.group)?.label ?? f.group} / ${f.caption}`,
        })),
    [template, field]
  );
  const [placeFieldId, setPlaceFieldId] = useState('');

  // Candidate "second line" fields for a dual-series chart: any other numeric
  // field in the template, labeled with its section so a long field list
  // stays navigable (e.g. "Heating - Infrared / Oven power (D)").
  const compareFieldOptions = useMemo(
    () =>
      template.fields
        .filter((f) => NUMERIC_TYPES.includes(f.datatype) && f.id !== field?.id)
        .map((f) => ({
          id: f.id,
          caption: f.caption,
          label: `${template.groups.find((g) => g.key === f.group)?.label ?? f.group} / ${f.caption}`,
        })),
    [template, field]
  );

  const update = <K extends keyof FieldDraft>(key: K, value: FieldDraft[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const handleKindChange = (kind: HMIFieldKind) => {
    setDraft((prev) => {
      let { datatype } = prev;
      if (kind === 'chart' && !NUMERIC_TYPES.includes(datatype)) datatype = 'f64';
      else if (kind === 'image') datatype = 'string';
      return {
        ...prev,
        kind,
        datatype,
        chartType: kind === 'chart' ? prev.chartType ?? 'line' : undefined,
        actionBehavior: kind === 'action' ? prev.actionBehavior ?? 'none' : undefined,
        overlayItems: kind === 'image' ? prev.overlayItems ?? [] : undefined,
      };
    });
  };

  const handleChartTypeChange = (chartType: FieldDraft['chartType']) => {
    setDraft((prev) => ({
      ...prev,
      chartType,
      // A gauge only ever shows one value, so drop any dual-line selection.
      compareFieldId: chartType === 'gauge' ? undefined : prev.compareFieldId,
      compareLabel: chartType === 'gauge' ? undefined : prev.compareLabel,
    }));
  };

  const handleCompareFieldChange = (compareFieldId: string) => {
    if (!compareFieldId) {
      setDraft((prev) => ({ ...prev, compareFieldId: undefined, compareLabel: undefined }));
      return;
    }
    const compareField = compareFieldOptions.find((f) => f.id === compareFieldId);
    setDraft((prev) => ({
      ...prev,
      compareFieldId,
      compareLabel: prev.compareLabel || compareField?.caption,
    }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      update('imageData', reader.result as string);
      update('imageName', file.name);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Clicking the preview pins whichever field is selected in "Field to place
  // next" at that spot (percentage of the image, so it stays correct at any
  // size). Clicking again with the same field selected repositions its pin
  // instead of adding a duplicate.
  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (!placeFieldId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    const items = draft.overlayItems ?? [];
    const idx = items.findIndex((it) => it.fieldId === placeFieldId);
    const placed: HMIImageOverlayItem = { fieldId: placeFieldId, x, y };
    const next = idx >= 0 ? items.map((it, i) => (i === idx ? placed : it)) : [...items, placed];
    update('overlayItems', next);
  };

  const removeOverlayItem = (fieldId: string) => {
    update('overlayItems', (draft.overlayItems ?? []).filter((it) => it.fieldId !== fieldId));
  };

  const addOption = () => {
    if (!optionDraft.label.trim()) return;
    const option: HMIFieldOption = { label: optionDraft.label.trim(), value: optionDraft.value };
    update('options', [...(draft.options ?? []), option]);
    setOptionDraft({ label: '', value: '' });
  };

  const removeOption = (index: number) => {
    update('options', (draft.options ?? []).filter((_, i) => i !== index));
  };

  const updateRestApiConfig = (patch: Partial<NonNullable<FieldDraft['restApiConfig']>>) => {
    setDraft((prev) => ({
      ...prev,
      restApiConfig: {
        method: 'POST',
        url: '',
        ...prev.restApiConfig,
        ...patch,
      },
    }));
  };

  const addHeader = () => {
    if (!headerDraft.key.trim()) return;
    updateRestApiConfig({
      headers: [...(draft.restApiConfig?.headers ?? []), { key: headerDraft.key.trim(), value: headerDraft.value }],
    });
    setHeaderDraft({ key: '', value: '' });
  };

  const removeHeader = (index: number) => {
    updateRestApiConfig({ headers: (draft.restApiConfig?.headers ?? []).filter((_, i) => i !== index) });
  };

  const addResponseMapping = () => {
    if (!mappingDraft.fieldId || !mappingDraft.responsePath.trim()) return;
    updateRestApiConfig({
      responseMappings: [
        ...(draft.restApiConfig?.responseMappings ?? []),
        { fieldId: mappingDraft.fieldId, responsePath: mappingDraft.responsePath.trim() },
      ],
    });
    setMappingDraft({ fieldId: '', responsePath: '' });
  };

  const removeResponseMapping = (index: number) => {
    updateRestApiConfig({
      responseMappings: (draft.restApiConfig?.responseMappings ?? []).filter((_, i) => i !== index),
    });
  };

  // Candidate target fields for a response mapping - any other field in the
  // template, labeled with its section like the chart "compare to" list.
  const responseMappingTargets = useMemo(
    () =>
      template.fields
        .filter((f) => f.id !== field?.id)
        .map((f) => ({
          id: f.id,
          caption: f.caption,
          label: `${template.groups.find((g) => g.key === f.group)?.label ?? f.group} / ${f.caption}`,
        })),
    [template, field]
  );

  const isValid = draft.caption.trim().length > 0;

  const handleSave = () => {
    if (!isValid) return;
    onSave({ ...draft, caption: draft.caption.trim() });
  };

  return (
    <Dialog open onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Caption"
            value={draft.caption}
            onChange={(e) => update('caption', e.target.value)}
            helperText="Label shown to the operator"
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Field type"
                value={draft.kind}
                onChange={(e) => handleKindChange(e.target.value as HMIFieldKind)}
              >
                {FIELD_KIND_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Data type"
                value={draft.datatype}
                onChange={(e) => update('datatype', e.target.value as HMIDataType)}
              >
                {(draft.kind === 'chart'
                  ? DATATYPE_OPTIONS.filter((o) => NUMERIC_TYPES.includes(o.value))
                  : DATATYPE_OPTIONS
                ).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Access"
                value={draft.access}
                onChange={(e) => update('access', e.target.value as HMIField['access'])}
              >
                {ACCESS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Source"
                value={draft.sourceKind}
                onChange={(e) => update('sourceKind', e.target.value as HMIField['sourceKind'])}
              >
                {SOURCE_KIND_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                label="Unit"
                value={draft.unit ?? ''}
                onChange={(e) => update('unit', e.target.value || undefined)}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Decimals"
                value={draft.decimals ?? ''}
                onChange={(e) =>
                  update('decimals', e.target.value === '' ? undefined : Number(e.target.value))
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                label="Default value"
                value={draft.default ?? ''}
                onChange={(e) => update('default', e.target.value)}
              />
            </Grid>
          </Grid>

          {/* Numeric range, reused by control sliders, array cell clamping, and chart/gauge bounds. */}
          {(NUMERIC_TYPES.includes(draft.datatype) || draft.datatype === 'array') && (
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Range min"
                  value={draft.rangeMin ?? ''}
                  onChange={(e) =>
                    update('rangeMin', e.target.value === '' ? undefined : Number(e.target.value))
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Range max"
                  value={draft.rangeMax ?? ''}
                  onChange={(e) =>
                    update('rangeMax', e.target.value === '' ? undefined : Number(e.target.value))
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Increment"
                  value={draft.increment ?? ''}
                  onChange={(e) =>
                    update(
                      'increment',
                      e.target.value === '' ? undefined : Number(e.target.value)
                    )
                  }
                  helperText={draft.kind === 'control' ? 'Set to render a slider' : undefined}
                />
              </Grid>
            </Grid>
          )}

          {/* Action behavior picker - gives "kind: action" buttons like Load/Save/Delete/
              Clear Process a built-in effect without writing code, instead of just logging
              a simulated click. See `HMIActionBehavior` for exactly what each option does. */}
          {draft.kind === 'action' && (
            <Box>
              <TextField
                select
                fullWidth
                size="small"
                label="Action behavior"
                value={draft.actionBehavior ?? 'none'}
                onChange={(e) => update('actionBehavior', e.target.value as FieldDraft['actionBehavior'])}
              >
                {ACTION_BEHAVIOR_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              {draft.actionBehavior && !['none', 'rest_api'].includes(draft.actionBehavior) && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Applies to every other field on this screen, except fields in this
                  button&apos;s own section - so a &quot;file management&quot; section with
                  these buttons can sit alongside the parameters it saves/loads/clears.
                </Alert>
              )}

              {/* REST API action editor - configures the HTTP call a button makes on
                  click (method/URL/headers/body), plus optional mapping of the JSON
                  response back onto other fields. See `HMIRestApiConfig`. */}
              {draft.actionBehavior === 'rest_api' && (
                <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                  <Alert severity="info">
                    URL, header values and body may reference other fields&apos; current
                    values with <code>{'{{field_id}}'}</code> placeholders. A URL starting
                    with <code>/</code> is called through the app&apos;s own authenticated
                    API client; any other URL is called directly.
                  </Alert>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      select
                      size="small"
                      label="Method"
                      sx={{ minWidth: 120 }}
                      value={draft.restApiConfig?.method ?? 'POST'}
                      onChange={(e) => updateRestApiConfig({ method: e.target.value as HMIRestApiMethod })}
                    >
                      {REST_API_METHOD_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      fullWidth
                      size="small"
                      label="URL"
                      placeholder="/solutions/{{twin_id}}/start or https://host/api/..."
                      value={draft.restApiConfig?.url ?? ''}
                      onChange={(e) => updateRestApiConfig({ url: e.target.value })}
                    />
                  </Stack>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Headers
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 0.5 }}>
                      {(draft.restApiConfig?.headers ?? []).map((h, idx) => (
                        <Stack key={`${h.key}-${idx}`} direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ flexGrow: 1 }}>
                            {h.key}: {h.value}
                          </Typography>
                          <IconButton size="small" onClick={() => removeHeader(idx)}>
                            <DeleteIcon fontSize="inherit" />
                          </IconButton>
                        </Stack>
                      ))}
                      <Stack direction="row" spacing={1}>
                        <TextField
                          size="small"
                          label="Header name"
                          value={headerDraft.key}
                          onChange={(e) => setHeaderDraft((p) => ({ ...p, key: e.target.value }))}
                        />
                        <TextField
                          size="small"
                          label="Value"
                          value={headerDraft.value}
                          onChange={(e) => setHeaderDraft((p) => ({ ...p, value: e.target.value }))}
                        />
                        <Button startIcon={<AddIcon />} onClick={addHeader}>
                          Add header
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>

                  {draft.restApiConfig?.method !== 'GET' && draft.restApiConfig?.method !== 'DELETE' && (
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      size="small"
                      label="Body (JSON)"
                      placeholder='{"name": "{{recipe_name}}"}'
                      value={draft.restApiConfig?.body ?? ''}
                      onChange={(e) => updateRestApiConfig({ body: e.target.value })}
                    />
                  )}

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Response mappings (optional)
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 0.5 }}>
                      {(draft.restApiConfig?.responseMappings ?? []).map((m, idx) => (
                        <Stack key={`${m.fieldId}-${idx}`} direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ flexGrow: 1 }}>
                            {m.responsePath} →{' '}
                            {responseMappingTargets.find((t) => t.id === m.fieldId)?.caption ?? m.fieldId}
                          </Typography>
                          <IconButton size="small" onClick={() => removeResponseMapping(idx)}>
                            <DeleteIcon fontSize="inherit" />
                          </IconButton>
                        </Stack>
                      ))}
                      <Stack direction="row" spacing={1}>
                        <TextField
                          size="small"
                          label="Response path"
                          placeholder="data.status"
                          value={mappingDraft.responsePath}
                          onChange={(e) => setMappingDraft((p) => ({ ...p, responsePath: e.target.value }))}
                        />
                        <TextField
                          select
                          size="small"
                          label="Target field"
                          sx={{ minWidth: 200 }}
                          value={mappingDraft.fieldId}
                          onChange={(e) => setMappingDraft((p) => ({ ...p, fieldId: e.target.value }))}
                        >
                          {responseMappingTargets.map((t) => (
                            <MenuItem key={t.id} value={t.id}>
                              {t.label}
                            </MenuItem>
                          ))}
                        </TextField>
                        <Button startIcon={<AddIcon />} onClick={addResponseMapping}>
                          Add mapping
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                </Stack>
              )}
            </Box>
          )}

          {/* Image-with-overlay widget editor: upload a picture, pick a field, then
              click a spot on it to pin that field's live value there. Mirrors the
              Digital Twin > Live Monitor "image property mapper" idea, but every pin
              just references an existing HMI field instead of its own property list. */}
          {draft.kind === 'image' && (
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <Button variant="outlined" size="small" component="label">
                  {draft.imageData ? 'Replace image' : 'Upload image'}
                  <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
                </Button>
                {draft.imageName && (
                  <Typography variant="caption" color="text.secondary">
                    {draft.imageName}
                  </Typography>
                )}
              </Stack>

              {!draft.imageData && (
                <Alert severity="info">
                  Upload an image, then pick a field below and click a spot on the picture
                  to pin that field&apos;s live value there.
                </Alert>
              )}

              {draft.imageData && (
                <>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Field to place next"
                    value={placeFieldId}
                    onChange={(e) => setPlaceFieldId(e.target.value)}
                    helperText="Pick a field, then click a spot on the image below to pin it there"
                    sx={{ mb: 1.5 }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {imageOverlayCandidates.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Box
                    sx={{
                      position: 'relative',
                      display: 'inline-block',
                      maxWidth: '100%',
                      lineHeight: 0,
                      cursor: placeFieldId ? 'crosshair' : 'default',
                    }}
                  >
                    <Box
                      component="img"
                      src={draft.imageData}
                      alt="Widget preview"
                      onClick={handleImageClick}
                      sx={{ maxWidth: '100%', display: 'block', borderRadius: 1 }}
                    />
                    {(draft.overlayItems ?? []).map((item) => {
                      const source = template.fields.find((f) => f.id === item.fieldId);
                      return (
                        <Box
                          key={item.fieldId}
                          sx={{
                            position: 'absolute',
                            left: `${item.x}%`,
                            top: `${item.y}%`,
                            transform: 'translate(-50%, -50%)',
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 3,
                            fontSize: 11,
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                          }}
                        >
                          {source?.caption ?? item.fieldId}
                        </Box>
                      );
                    })}
                  </Box>

                  <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                    {(draft.overlayItems ?? []).length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        No pins placed yet.
                      </Typography>
                    )}
                    {(draft.overlayItems ?? []).map((item) => {
                      const source = template.fields.find((f) => f.id === item.fieldId);
                      return (
                        <Stack key={item.fieldId} direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ flexGrow: 1 }}>
                            {source?.caption ?? item.fieldId} ({item.x.toFixed(0)}%, {item.y.toFixed(0)}%)
                          </Typography>
                          <IconButton size="small" onClick={() => removeOverlayItem(item.fieldId)}>
                            <DeleteIcon fontSize="inherit" />
                          </IconButton>
                        </Stack>
                      );
                    })}
                  </Stack>
                </>
              )}
            </Box>
          )}

          {/* Enum options editor, for control/indicator fields backed by a fixed list of values. */}
          {draft.datatype === 'enum' && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Options
              </Typography>
              <Stack spacing={1} sx={{ mt: 0.5 }}>
                {(draft.options ?? []).map((opt, idx) => (
                  <Stack key={`${opt.label}-${idx}`} direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      {opt.label} ({String(opt.value)})
                    </Typography>
                    <IconButton size="small" onClick={() => removeOption(idx)}>
                      <DeleteIcon fontSize="inherit" />
                    </IconButton>
                  </Stack>
                ))}
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    label="Label"
                    value={optionDraft.label}
                    onChange={(e) => setOptionDraft((p) => ({ ...p, label: e.target.value }))}
                  />
                  <TextField
                    size="small"
                    label="Value"
                    value={optionDraft.value}
                    onChange={(e) => setOptionDraft((p) => ({ ...p, value: e.target.value }))}
                  />
                  <Button startIcon={<AddIcon />} onClick={addOption}>
                    Add option
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}

          {/* Array grid dimensions, for control/indicator fields rendered as a matrix. */}
          {draft.datatype === 'array' && (
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Array of"
                  value={draft.arrayOf ?? 'f32'}
                  onChange={(e) => update('arrayOf', e.target.value as HMIField['arrayOf'])}
                >
                  {ARRAY_OF_OPTIONS.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Length"
                  value={draft.arrayLength ?? ''}
                  onChange={(e) =>
                    update(
                      'arrayLength',
                      e.target.value === '' ? undefined : Number(e.target.value)
                    )
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Grid rows"
                  value={draft.gridRows ?? ''}
                  onChange={(e) =>
                    update('gridRows', e.target.value === '' ? undefined : Number(e.target.value))
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Grid cols"
                  value={draft.gridCols ?? ''}
                  onChange={(e) =>
                    update('gridCols', e.target.value === '' ? undefined : Number(e.target.value))
                  }
                />
              </Grid>
            </Grid>
          )}

          {/* Chart/widget options - same chart-type vocabulary as the dashboard "Add widget" flow. */}
          {draft.kind === 'chart' && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Chart type"
                  value={draft.chartType ?? 'line'}
                  onChange={(e) => handleChartTypeChange(e.target.value as HMIField['chartType'])}
                >
                  {HMI_CHART_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="History window (s)"
                  value={draft.chartWindowSec ?? ''}
                  placeholder="60"
                  onChange={(e) =>
                    update(
                      'chartWindowSec',
                      e.target.value === '' ? undefined : Number(e.target.value)
                    )
                  }
                  helperText="How much trend history to keep on screen"
                />
              </Grid>
              {draft.chartType !== 'gauge' && (
                <>
                  <Grid item xs={6}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Compare to (second line, optional)"
                      value={draft.compareFieldId ?? ''}
                      onChange={(e) => handleCompareFieldChange(e.target.value)}
                      helperText="Plots a second field on the same chart, e.g. set-point vs. actual"
                    >
                      <MenuItem value="">None</MenuItem>
                      {compareFieldOptions.map((opt) => (
                        <MenuItem key={opt.id} value={opt.id}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Compare label"
                      disabled={!draft.compareFieldId}
                      value={draft.compareLabel ?? ''}
                      onChange={(e) => update('compareLabel', e.target.value || undefined)}
                      helperText="Legend text for the second line"
                    />
                  </Grid>
                </>
              )}
            </Grid>
          )}


          <Accordion disableGutters variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2">Advanced (polling, rules, traceability)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Update rate (Hz)"
                    value={draft.updateRateHz ?? ''}
                    onChange={(e) =>
                      update(
                        'updateRateHz',
                        e.target.value === '' ? undefined : Number(e.target.value)
                      )
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Enabled when"
                    value={draft.enabledWhen ?? ''}
                    placeholder='e.g. heating_method == "infrared"'
                    onChange={(e) => update('enabledWhen', e.target.value || undefined)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Test_Info.ini key"
                    value={draft.testInfoKey ?? ''}
                    onChange={(e) => update('testInfoKey', e.target.value || undefined)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Recipe path"
                    value={draft.recipePath ?? ''}
                    onChange={(e) => update('recipePath', e.target.value || undefined)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    label="Notes"
                    value={draft.notes ?? ''}
                    onChange={(e) => update('notes', e.target.value || undefined)}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {!isValid && (
            <Alert severity="info" variant="outlined">
              Caption is required.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        <Box>
          {isEditing && onDelete && (
            <Button color="error" startIcon={<DeleteIcon />} onClick={onDelete}>
              Delete field
            </Button>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button variant="contained" disabled={!isValid} onClick={handleSave}>
            Save
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

export default FieldEditorDialog;
